import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import SearchIcon from "@mui/icons-material/Search";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import type {
  HelpDeskTicket,
  TicketStatus,
  TicketPriority,
} from "../../Models/helpDesk";
import { useHelpDeskAccess, useMyTickets } from "../../Query/useHelpDesk";
import color from "../../Constants/colors";
import CreateTicketModal from "./CreateTicketModal";
import TicketDetailsModal from "./TicketDetailsModal";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";

const panel = {
  bgcolor: color.white,
  border: `1px solid ${color.gray200}`,
  borderRadius: "13px",
  boxShadow: "0 3px 14px rgba(3,46,77,0.04)",
};
const heading = {
  color: `${color.primary_dark} !important`,
  fontWeight: 800,
  fontSize: 16,
};
const buttonStyle = {
  bgcolor: `${color.accent} !important`,
  color: `${color.white} !important`,
  WebkitTextFillColor: `${color.white} !important`,
  textTransform: "none" as const,
  borderRadius: "9px",
  fontWeight: 700,
  boxShadow: "none",
  "&:hover": {
    bgcolor: `${color.accent_dark} !important`,
  },
  "&.Mui-disabled": {
    bgcolor: `${color.gray200} !important`,
    color: `${color.gray500} !important`,
    WebkitTextFillColor: `${color.gray500} !important`,
  },
};
const pageStyle = {
  bgcolor: color.gray50,
  minHeight: "100vh",
  color: color.black,
  "& .MuiTypography-root": {
    fontFamily: "inherit",
  },
  "& .MuiOutlinedInput-root": {
    bgcolor: `${color.white} !important`,
    color: `${color.black} !important`,
    borderRadius: "9px",
  },
  "& .MuiInputBase-input, & textarea": {
    color: `${color.black} !important`,
    WebkitTextFillColor: `${color.black} !important`,
  },
  "& .MuiInputLabel-root": {
    color: `${color.gray500} !important`,
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: `${color.accent_dark} !important`,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: `${color.gray200} !important`,
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: `${color.accent} !important`,
  },
  "& .MuiFormHelperText-root": {
    color: `${color.gray500} !important`,
  },
  "& .MuiSelect-icon": {
    color: `${color.primary_dark} !important`,
  },
};
const HelpDeskUser: React.FC = () => {
  const { t } = useTranslation("helpDesk");
  const faq = [
    {
      question: t("faq.createQuestion"),
      answer: t("faq.createAnswer"),
    },
    {
      question: t("faq.trackQuestion"),
      answer: t("faq.trackAnswer"),
    },
    {
      question: t("faq.replyQuestion"),
      answer: t("faq.replyAnswer"),
    },
    {
      question: t("faq.statusQuestion"),
      answer: t("faq.statusAnswer"),
    },
    {
      question: t("faq.priorityQuestion"),
      answer: t("faq.priorityAnswer"),
    },
    {
      question: t("faq.resolvedQuestion"),
      answer: t("faq.resolvedAnswer"),
    },
  ];
  const [createOpen, setCreateOpen] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: access } = useHelpDeskAccess();
  // Email links open a ticket directly: /help-desk?ticket=<id>
  const [selectedId, setSelectedId] = useState<number | undefined>(() => {
    const id = Number(searchParams.get("ticket"));
    return Number.isInteger(id) && id > 0 ? id : undefined;
  });
  const closeDetails = () => {
    setSelectedId(undefined);
    if (searchParams.has("ticket")) {
      searchParams.delete("ticket");
      setSearchParams(searchParams, { replace: true });
    }
  };
  const [search, setSearch] = useState("");
  const { data: tickets = [], isLoading, isError, refetch } = useMyTickets();
  const statusLabels: Record<TicketStatus, string> = {
    0: t("status.submitted"),
    1: t("status.inProgress"),
    2: t("status.closed"),
  };
  const priorityLabels: Record<TicketPriority, string> = {
    0: t("priority.low"),
    1: t("priority.normal"),
    2: t("priority.high"),
  };
  const statusColors = [
    { bg: color.orange50, text: color.accent_dark },
    { bg: color.blue50, text: color.secondary },
    { bg: color.green50, text: color.green700 },
  ];
  const priorityColors = [
    { bg: color.gray50, text: color.gray500 },
    { bg: color.blue50, text: color.secondary },
    { bg: color.red50, text: color.red700 },
  ];
  const chip = (label: string, bg: string, text: string) => (
    <Chip
      size="small"
      label={label}
      sx={{
        height: 21,
        borderRadius: "5px",
        fontSize: 10,
        fontWeight: 700,
        bgcolor: `${bg} !important`,
        color: `${text} !important`,
        "& .MuiChip-label": {
          px: 0.8,
          color: "inherit !important",
        },
      }}
    />
  );
  const statusChip = (value: TicketStatus) => {
    const style = statusColors[value] ?? statusColors[0];
    return chip(
      statusLabels[value] ?? t("status.unknown"),
      style.bg,
      style.text,
    );
  };
  const priorityChip = (value: TicketPriority) => {
    const style = priorityColors[value] ?? priorityColors[1];
    return chip(
      priorityLabels[value] ?? t("priority.normal"),
      style.bg,
      style.text,
    );
  };
  const filtered = tickets.filter((item) =>
    [item.id, item.title, item.category, item.description, ...(item.tags ?? [])]
      .join(" ")
      .toLowerCase()
      .includes(search.toLowerCase()),
  );
  const stats = [
    {
      label: t("summary.totalTickets"),
      value: tickets.length,
      icon: <AssignmentIcon fontSize="small" />,
      bg: color.blue50,
      iconColor: color.secondary,
    },
    {
      label: t("summary.inProgress"),
      value: tickets.filter((x) => x.status === 1).length,
      icon: <AccessTimeIcon fontSize="small" />,
      bg: color.orange50,
      iconColor: color.accent_dark,
    },
    {
      label: t("summary.closed"),
      value: tickets.filter((x) => x.status === 2).length,
      icon: <CheckCircleIcon fontSize="small" />,
      bg: color.green50,
      iconColor: color.green700,
    },
  ];
  return (
    <Box sx={{ ...pageStyle, p: { xs: 2, md: 3 } }}>
      <Box
        sx={{
          background: `linear-gradient(110deg, ${color.blue50}, ${color.white} 72%, ${color.orange50})`,
          border: `1px solid ${color.gray200}`,
          borderRadius: "15px",
          p: { xs: 2.5, md: 3 },
          mb: 2,
        }}
      >
        <Typography
          sx={{
            color: `${color.secondary} !important`,
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: 1.2,
          }}
        >
          {t("title")}
        </Typography>
        <Typography
          sx={{
            color: `${color.primary_dark} !important`,
            fontSize: { xs: 24, md: 30 },
            fontWeight: 800,
            mt: 0.5,
          }}
        >
          {t("help")}
        </Typography>
        <Typography
          sx={{
            color: `${color.gray500} !important`,
            fontSize: 12,
            mt: 0.5,
            mb: 2,
          }}
        >
          {t("subtitle")}
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
          <TextField
            size="small"
            placeholder={t("searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              flex: 1,
              "& .MuiInputBase-input": {
                fontSize: "14px",
              },
            }}
            InputProps={{
              startAdornment: (
                <SearchIcon
                  sx={{
                    mr: 1,
                    fontSize: 19,
                    color: color.secondary,
                  }}
                />
              ),
            }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
            sx={{
              ...buttonStyle,
              px: 2.5,
              whiteSpace: "nowrap",
              fontSize: 12,
            }}
          >
            {t("createNewTicket")}
          </Button>
          <Button
            variant="outlined"
            startIcon={<MenuBookOutlinedIcon />}
            onClick={() => navigate("/features_catalog")}
            sx={{
              px: 2.5,
              whiteSpace: "nowrap",
              fontSize: 12,
              fontWeight: 700,
              textTransform: "none",
              borderRadius: "9px",
              color: `${color.secondary} !important`,
              borderColor: `${color.gray200} !important`,
              bgcolor: `${color.white} !important`,
              transition: "all 0.2s ease",
              "&:hover": {
                bgcolor: `${color.blue50} !important`,
                borderColor: `${color.secondary} !important`,
              },
            }}
          >
            {t("helpCenter")}
          </Button>
          {access?.is_help_desk && (
            <Button
              variant="outlined"
              startIcon={<ManageSearchIcon />}
              onClick={() => navigate("/help-desk-admin")}
              sx={{
                px: 2.5,
                whiteSpace: "nowrap",
                fontSize: 12,
                fontWeight: 700,
                textTransform: "none",
                borderRadius: "9px",
                color: `${color.secondary} !important`,
                borderColor: `${color.secondary} !important`,
              }}
            >
              {t("admin.manageTicketsButton")}
            </Button>
          )}
        </Stack>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            lg: "minmax(0, 1fr) 320px",
          },
          gap: 2,
          alignItems: "start",
        }}
      >
        <Stack spacing={2} sx={{ minWidth: 0 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(3, minmax(0, 1fr))",
              },
              gap: 1.5,
            }}
          >
            {stats.map((item) => (
              <Box
                key={item.label}
                sx={{
                  ...panel,
                  p: 1.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    flexShrink: 0,
                    borderRadius: "9px",
                    bgcolor: item.bg,
                    color: item.iconColor,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {item.icon}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      color: `${color.gray500} !important`,
                      fontSize: 10.5,
                      fontWeight: 600,
                    }}
                  >
                    {item.label}
                  </Typography>
                  <Typography
                    sx={{
                      color: `${color.primary_dark} !important`,
                      fontSize: 21,
                      fontWeight: 800,
                      lineHeight: 1.2,
                    }}
                  >
                    {item.value}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
          <Box sx={{ ...panel, p: { xs: 1.25, md: 2 } }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              mb={1.5}
            >
              <Box>
                <Typography sx={heading}>{t("tickets.title")}</Typography>
                <Typography
                  sx={{
                    color: `${color.gray500} !important`,
                    fontSize: 11,
                  }}
                >
                  {t("tickets.subtitle")}
                </Typography>
              </Box>
              <Typography
                sx={{
                  color: `${color.secondary} !important`,
                  fontSize: 11,
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                }}
              >
                {t("tickets.count", { count: filtered.length })}
              </Typography>
            </Stack>
            {isLoading ? (
              <Box textAlign="center" py={5}>
                <CircularProgress size={28} sx={{ color: color.accent }} />
              </Box>
            ) : isError ? (
              <Alert
                severity="error"
                action={
                  <Button onClick={() => refetch()}>
                    {t("actions.retry")}
                  </Button>
                }
              >
                {t("tickets.loadError")}
              </Alert>
            ) : filtered.length === 0 ? (
              <Box textAlign="center" py={4}>
                <Typography
                  sx={{
                    color: `${color.gray500} !important`,
                    mb: 2,
                  }}
                >
                  {search
                    ? t("tickets.noMatches")
                    : t("tickets.emptyDescription")}
                </Typography>
                {!search && (
                  <Button
                    variant="contained"
                    sx={buttonStyle}
                    onClick={() => setCreateOpen(true)}
                  >
                    {t("tickets.createFirstTicket")}
                  </Button>
                )}
              </Box>
            ) : (
              <Box sx={{ overflowX: "auto" }}>
                <Box
                  component="table"
                  sx={{
                    width: "100%",
                    minWidth: 590,
                    tableLayout: "fixed",
                    borderCollapse: "collapse",
                    "& th": {
                      textAlign: "left",
                      fontSize: 10,
                      fontWeight: 800,
                      color: `${color.gray500} !important`,
                      bgcolor: color.gray50,
                      px: 0.75,
                      py: 1,
                      whiteSpace: "nowrap",
                      borderBottom: `1px solid ${color.gray200}`,
                    },
                    "& td": {
                      px: 0.75,
                      py: 0.9,
                      fontSize: 10.5,
                      color: `${color.primary_dark} !important`,
                      borderBottom: `1px solid ${color.gray200}`,
                      verticalAlign: "middle",
                      overflow: "hidden",
                    },
                    "& tbody tr": {
                      cursor: "pointer",
                      transition: "background .15s",
                      "&:hover": {
                        bgcolor: color.orange50,
                      },
                    },
                    "& tbody tr:last-child td": {
                      borderBottom: "none",
                    },
                  }}
                >
                  <colgroup>
                    <col style={{ width: "8%" }} />
                    <col style={{ width: "20%" }} />
                    <col style={{ width: "17%" }} />
                    <col style={{ width: "15%" }} />
                    <col style={{ width: "18%" }} />
                    <col style={{ width: "18%" }} />
                    <col style={{ width: "4%" }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>{t("table.id")}</th>
                      <th>{t("table.title")}</th>
                      <th>{t("table.category")}</th>
                      <th>{t("table.tags")}</th>
                      <th>{t("table.status")}</th>
                      <th>{t("table.priority")}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((item: HelpDeskTicket) => (
                      <tr key={item.id} onClick={() => setSelectedId(item.id)}>
                        <td>
                          <Typography
                            sx={{
                              color: `${color.secondary} !important`,
                              fontSize: 10.5,
                              fontWeight: 800,
                            }}
                          >
                            #{item.id}
                          </Typography>
                        </td>
                        <td>
                          <Tooltip title={item.title}>
                            <Typography
                              sx={{
                                color: `${color.primary_dark} !important`,
                                fontSize: 10.5,
                                fontWeight: 700,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {item.title}
                            </Typography>
                          </Tooltip>
                          <Typography
                            sx={{
                              color: `${color.gray500} !important`,
                              fontSize: 9.5,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {item.workspace?.name ?? t("tickets.general")}
                          </Typography>
                        </td>
                        <td>
                          <Tooltip title={item.category || ""}>
                            <Typography
                              sx={{
                                color: `${color.gray500} !important`,
                                fontSize: 10.5,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {item.category || "/"}
                            </Typography>
                          </Tooltip>
                        </td>
                        <td>
                          <Stack
                            direction="row"
                            spacing={0.4}
                            useFlexGap
                            flexWrap="wrap"
                          >
                            {item.tags?.length ? (
                              item.tags.map((tag, index) => (
                                <Tooltip key={`${tag}-${index}`} title={tag}>
                                  <Chip
                                    label={`#${tag}`}
                                    size="small"
                                    sx={{
                                      height: 19,
                                      maxWidth: "100%",
                                      borderRadius: "4px",
                                      bgcolor: `${color.orange50} !important`,
                                      color: `${color.accent_dark} !important`,
                                      fontSize: 9,
                                      fontWeight: 700,
                                      "& .MuiChip-label": {
                                        px: 0.65,
                                        color: "inherit !important",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                      },
                                    }}
                                  />
                                </Tooltip>
                              ))
                            ) : (
                              <Typography
                                sx={{
                                  color: `${color.gray500} !important`,
                                  fontSize: 10,
                                }}
                              >
                                /
                              </Typography>
                            )}
                          </Stack>
                        </td>
                        <td>{statusChip(item.status)}</td>
                        <td>{priorityChip(item.priority)}</td>
                        <td>
                          <ArrowForwardIcon
                            sx={{
                              color: color.secondary,
                              fontSize: 15,
                            }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Box>
              </Box>
            )}
          </Box>
        </Stack>
        <Stack spacing={2}>
          <Box sx={{ ...panel, p: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
              <HelpOutlineIcon sx={{ color: color.accent, fontSize: 20 }} />
              <Typography sx={heading}>{t("faq.title")}</Typography>
            </Stack>
            {faq.map((item, index) => (
              <Accordion
                key={index}
                disableGutters
                elevation={0}
                sx={{
                  bgcolor: `${color.white} !important`,
                  color: `${color.primary_dark} !important`,
                  borderBottom: `1px solid ${color.gray200}`,
                  "&:before": {
                    display: "none",
                  },
                  "&:last-child": {
                    borderBottom: "none",
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <ExpandMoreIcon
                      sx={{
                        color: color.secondary,
                        fontSize: 18,
                      }}
                    />
                  }
                  sx={{
                    px: 0,
                    minHeight: 42,
                    "& .MuiAccordionSummary-content": {
                      my: 1,
                    },
                  }}
                >
                  <Typography
                    sx={{
                      color: `${color.primary_dark} !important`,
                      fontSize: 11.5,
                      fontWeight: 700,
                    }}
                  >
                    {item.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 0, pt: 0 }}>
                  <Typography
                    sx={{
                      color: `${color.gray500} !important`,
                      fontSize: 11,
                      lineHeight: 1.6,
                    }}
                  >
                    {item.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Stack>
      </Box>
      <CreateTicketModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => refetch()}
      />
      <TicketDetailsModal ticketId={selectedId} onClose={closeDetails} />
    </Box>
  );
};
export default HelpDeskUser;
